/**
 * Feature Banner Component
 *
 * A dismissible banner for displaying release notes and feature spotlights.
 * Shows contextual messages at the top of modals, pages, or sections.
 *
 * Features:
 * - Fetches the highest priority active doc for a given context trigger
 * - Filters out docs the user has already dismissed
 * - Persists dismissal to Firestore (users/{userId}/dismissedDocs)
 * - Supports "Read More" to open the full doc in the help panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Lightbulb, Rocket, Zap, Star, Gift, Megaphone, Bell, LucideIcon } from 'lucide-react';
import { DocumentationService } from '../services/DocumentationService';
import { DocumentationDoc } from '../types/documentation.types';
// @ts-ignore - Module Federation dynamic import
import { auth } from 'shell/Firebase';

// Spotlight color styles using Tailwind classes (solid dark backgrounds)
const SPOTLIGHT_COLOR_STYLES: Record<string, {
  bg: string;
  border: string;
  icon: string;
  title: string;
  content: string;
}> = {
  cyan: {
    bg: 'bg-cyan-950',
    border: 'border-cyan-800',
    icon: 'text-cyan-400',
    title: 'text-cyan-400',
    content: 'text-cyan-200',
  },
  purple: {
    bg: 'bg-purple-950',
    border: 'border-purple-800',
    icon: 'text-purple-400',
    title: 'text-purple-400',
    content: 'text-purple-200',
  },
  green: {
    bg: 'bg-green-950',
    border: 'border-green-800',
    icon: 'text-green-400',
    title: 'text-green-400',
    content: 'text-green-200',
  },
  amber: {
    bg: 'bg-amber-950',
    border: 'border-amber-800',
    icon: 'text-amber-400',
    title: 'text-amber-400',
    content: 'text-amber-200',
  },
  rose: {
    bg: 'bg-rose-950',
    border: 'border-rose-800',
    icon: 'text-rose-400',
    title: 'text-rose-400',
    content: 'text-rose-200',
  },
  blue: {
    bg: 'bg-blue-950',
    border: 'border-blue-800',
    icon: 'text-blue-400',
    title: 'text-blue-400',
    content: 'text-blue-200',
  },
};

// Spotlight icon components
const SPOTLIGHT_ICON_COMPONENTS: Record<string, LucideIcon> = {
  sparkles: Sparkles,
  lightbulb: Lightbulb,
  rocket: Rocket,
  zap: Zap,
  star: Star,
  gift: Gift,
  megaphone: Megaphone,
  bell: Bell,
};

interface FeatureBannerProps {
  /** The context trigger to fetch docs for (e.g., "episode-composer:article-picker") */
  contextTrigger: string;

  /** Optional callback when the banner is dismissed */
  onDismiss?: () => void;

  /** Optional callback when "Read More" is clicked */
  onReadMore?: (doc: DocumentationDoc) => void;

  /** Optional className for styling */
  className?: string;
}

// Extended doc type that includes spotlight color, icon, standalone flag, release version, and category
type ExtendedDoc = DocumentationDoc & {
  spotlightColor?: string;
  spotlightIcon?: string;
  isStandalone?: boolean;
  releaseVersion?: string;
  spotlightCategory?: string;
};

export const FeatureBanner: React.FC<FeatureBannerProps> = ({
  contextTrigger,
  onDismiss,
  onReadMore,
  className = '',
}) => {
  const [doc, setDoc] = useState<ExtendedDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  // Get userId directly - user is already authenticated when this component mounts
  const userId = auth?.currentUser?.uid || null;

  // Fetch active, undismissed doc for this context
  useEffect(() => {
    let cancelled = false;

    const fetchDoc = async () => {
      if (!userId) {
        console.log('[FeatureBanner] No userId available');
        setLoading(false);
        return;
      }

      try {
        const activeDoc = await DocumentationService.getUndismissedReleaseNoteForContext(
          userId,
          contextTrigger
        );

        if (!cancelled) {
          setDoc(activeDoc);
          setLoading(false);
        }
      } catch (error) {
        console.error('[FeatureBanner] Error fetching doc:', error);
        if (!cancelled) {
          setDoc(null);
          setLoading(false);
        }
      }
    };

    fetchDoc();

    return () => {
      cancelled = true;
    };
  }, [userId, contextTrigger]);

  // Handle dismiss
  const handleDismiss = useCallback(async () => {
    if (!userId || !doc) return;

    setDismissing(true);
    try {
      // Check if this is a standalone spotlight
      if (doc.isStandalone) {
        // Standalone spotlight - dismissal format: standalone_{spotlightId}
        await DocumentationService.dismissSpotlight(userId, 'standalone', doc.id);
      } else if (doc.category === 'spotlight') {
        // Check if this is a release-based spotlight (id format: releaseId_spotlightId)
        // Release-based spotlights are created with this id pattern in DocumentationService
        const parts = doc.id.split('_');
        if (parts.length >= 2) {
          // This is a release-based spotlight - use the new dismiss method
          // Format is releaseId_spotlightId where releaseId could contain underscores
          const lastUnderscoreIdx = doc.id.lastIndexOf('_');
          const releaseId = doc.id.substring(0, lastUnderscoreIdx);
          const spotlightId = doc.id.substring(lastUnderscoreIdx + 1);
          await DocumentationService.dismissSpotlight(userId, releaseId, spotlightId);
        } else {
          // Fallback to legacy dismiss
          await DocumentationService.dismissDoc(userId, doc.id);
        }
      } else {
        // This is a legacy documentation-based doc - use the old dismiss method
        await DocumentationService.dismissDoc(userId, doc.id);
      }

      // Re-fetch to get the next undismissed spotlight (instead of just hiding)
      const nextDoc = await DocumentationService.getUndismissedReleaseNoteForContext(
        userId,
        contextTrigger
      );

      if (nextDoc) {
        // Show the next spotlight with animation
        setDoc(nextDoc);
        setAnimationKey(prev => prev + 1);  // Trigger re-animation
      } else {
        // No more spotlights - hide the banner
        setDismissed(true);
      }

      onDismiss?.();
    } catch (error) {
      console.error('[FeatureBanner] Error dismissing doc:', error);
    } finally {
      setDismissing(false);
    }
  }, [userId, doc, contextTrigger, onDismiss]);

  // Don't render if loading, no doc, or dismissed
  if (loading || !doc || dismissed) {
    return null;
  }

  // Get styles - use spotlight color if available, otherwise fall back to priority-based
  const getStyles = () => {
    // Check for spotlight-specific color
    if (doc.spotlightColor && SPOTLIGHT_COLOR_STYLES[doc.spotlightColor]) {
      return SPOTLIGHT_COLOR_STYLES[doc.spotlightColor];
    }

    // Fall back to priority-based styling for legacy docs
    switch (doc.priority) {
      case 'critical':
        return {
          bg: 'bg-red-950',
          border: 'border-red-800',
          icon: 'text-red-400',
          title: 'text-red-400',
          content: 'text-red-200',
        };
      case 'high':
        return {
          bg: 'bg-amber-950',
          border: 'border-amber-800',
          icon: 'text-amber-400',
          title: 'text-amber-400',
          content: 'text-amber-200',
        };
      default:
        return {
          bg: 'bg-cyan-950',
          border: 'border-cyan-800',
          icon: 'text-cyan-400',
          title: 'text-cyan-400',
          content: 'text-cyan-200',
        };
    }
  };

  const styles = getStyles();

  // Get icon component - use spotlight icon if available
  const IconComponent = doc.spotlightIcon && SPOTLIGHT_ICON_COMPONENTS[doc.spotlightIcon]
    ? SPOTLIGHT_ICON_COMPONENTS[doc.spotlightIcon]
    : Sparkles;

  // Truncate content for display
  const truncatedContent = doc.content.length > 150
    ? doc.content.substring(0, 150).trim() + '...'
    : doc.content;

  // Strip markdown for plain text display
  const plainContent = truncatedContent
    .replace(/#{1,6}\s/g, '')  // Remove headers
    .replace(/\*\*/g, '')      // Remove bold
    .replace(/\*/g, '')        // Remove italic
    .replace(/`/g, '')         // Remove code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Replace links with text

  return (
    <div
      key={animationKey}
      className={`feature-banner ${styles.bg} ${styles.border} rounded-lg p-4 mb-4 flex flex-col gap-3 border animate-feature-banner-slide ${className}`}
    >
      {/* Icon + Content row */}
      <div className="flex gap-3 items-start">
        {/* Icon */}
        <div className="shrink-0 w-8 h-8 rounded-md bg-black/20 flex items-center justify-center">
          <IconComponent size={18} className={styles.icon} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className={`font-semibold text-lg mb-1 ${styles.title}`}>
            {doc.title}
          </div>
          <div className={`text-sm leading-relaxed ${styles.content}`}>
            {plainContent}
          </div>
        </div>
      </div>

      {/* Dismiss button row */}
      <div className="flex justify-end">
        <button
          onClick={handleDismiss}
          disabled={dismissing}
          className={`shrink-0 rounded-md ${styles.border} border bg-transparent ${styles.title} cursor-pointer py-1 px-2 text-sm transition-all hover:opacity-80 ${dismissing ? 'opacity-50' : ''}`}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default FeatureBanner;
